import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar, BarChart, Package } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import LiveCard from '@/components/LiveCard';

const SellerDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [myLives, setMyLives] = useState([]);
  const [activeTab, setActiveTab] = useState('schedule');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    platform: 'instagram',
    scheduled_at: '',
    duration: 60,
    category: 'fashion',
    stream_url: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchSellerData();
  }, [user, navigate]);

  const fetchSellerData = async () => {
    try {
      const sellersRes = await api.get('/sellers', { params: { approved_only: false } });
      const mySeller = sellersRes.data.find(s => s.user_id === user.user_id);
      
      if (!mySeller) {
        // Not a seller yet
        return;
      }

      setSeller(mySeller);

      const livesRes = await api.get('/lives/week');
      const mySchedules = livesRes.data.filter(l => l.seller_id === mySeller.seller_id);
      setMyLives(mySchedules);
    } catch (error) {
      console.error('Failed to fetch seller data:', error);
    }
  };

  const handleCreateLive = async (e) => {
    e.preventDefault();
    try {
      await api.post('/lives/schedule', null, { params: formData });
      setShowCreateForm(false);
      setFormData({
        title: '',
        description: '',
        platform: 'instagram',
        scheduled_at: '',
        duration: 60,
        category: 'fashion',
        stream_url: ''
      });
      fetchSellerData();
    } catch (error) {
      console.error('Failed to create live schedule:', error);
      toast.error(error.response?.data?.detail || '라이브 스케줄 생성에 실패했습니다.');
    }
  };

  const handleRegisterSeller = async (e) => {
    e.preventDefault();
    const name = prompt('판매자 이름을 입력하세요:');
    const bio = prompt('소개를 입력하세요:');
    const categories = prompt('카테고리를 입력하세요 (쉼표로 구분):').split(',').map(c => c.trim());

    try {
      await api.post('/sellers/register', null, { params: { name, bio, categories } });
      toast.success('판매자 등록 신청이 완료되었습니다. 관리자 승인을 기다려주세요.');
      fetchSellerData();
    } catch (error) {
      console.error('Failed to register seller:', error);
      toast.error(error.response?.data?.detail || '판매자 등록에 실패했습니다.');
    }
  };

  if (!user) return null;

  if (!seller) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold mb-4">판매자 등록</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            판매자로 등록하고 라이브 방송을 시작하세요!
          </p>
          <button
            onClick={handleRegisterSeller}
            className="px-6 py-3 bg-[#FF3B30] text-white font-bold rounded-md hover:bg-[#D32F2F] transition-colors"
            data-testid="register-seller-button"
          >
            판매자 등록하기
          </button>
        </div>
      </div>
    );
  }

  if (!seller.is_approved) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold mb-4">승인 대기 중</h2>
          <p className="text-gray-600 dark:text-gray-400">
            판매자 승인을 기다리고 있습니다. 곧 연락드리겠습니다.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="seller-dashboard-title">
              판매자 대시보드
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {seller.follower_count?.toLocaleString()} 팔로워
            </p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-6 py-3 bg-[#FF3B30] text-white font-bold rounded-md hover:bg-[#D32F2F] transition-colors flex items-center space-x-2"
            data-testid="create-live-button"
          >
            <Plus size={20} />
            <span>라이브 등록</span>
          </button>
        </div>

        {/* Create Live Form */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#141414] rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4">라이브 스케줄 등록</h2>
              <form onSubmit={handleCreateLive} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">제목</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-2 bg-white dark:bg-[#0A0A0A] border border-black/10 dark:border-white/10 rounded-md"
                    required
                    data-testid="live-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">설명</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-4 py-2 bg-white dark:bg-[#0A0A0A] border border-black/10 dark:border-white/10 rounded-md"
                    rows={3}
                    required
                    data-testid="live-description-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">플랫폼</label>
                  <select
                    value={formData.platform}
                    onChange={(e) => setFormData({...formData, platform: e.target.value})}
                    className="w-full px-4 py-2 bg-white dark:bg-[#0A0A0A] border border-black/10 dark:border-white/10 rounded-md"
                    data-testid="live-platform-input"
                  >
                    <option value="instagram">Instagram</option>
                    <option value="tiktok">TikTok</option>
                    <option value="youtube">YouTube</option>
                    <option value="naver">Naver</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">일시</label>
                  <input
                    type="datetime-local"
                    value={formData.scheduled_at}
                    onChange={(e) => setFormData({...formData, scheduled_at: e.target.value})}
                    className="w-full px-4 py-2 bg-white dark:bg-[#0A0A0A] border border-black/10 dark:border-white/10 rounded-md"
                    required
                    data-testid="live-scheduled-at-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">시간 (분)</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 bg-white dark:bg-[#0A0A0A] border border-black/10 dark:border-white/10 rounded-md"
                    min="30"
                    required
                    data-testid="live-duration-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">카테고리</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-2 bg-white dark:bg-[#0A0A0A] border border-black/10 dark:border-white/10 rounded-md"
                    data-testid="live-category-input"
                  >
                    <option value="fashion">패션</option>
                    <option value="beauty">뷰티</option>
                    <option value="food">푸드</option>
                    <option value="pet">펫</option>
                    <option value="kids">키즈</option>
                    <option value="electronics">전자제품</option>
                    <option value="home">홈</option>
                    <option value="health">헬스</option>
                    <option value="sports">스포츠</option>
                    <option value="others">기타</option>
                  </select>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-[#FF3B30] text-white font-bold rounded-md hover:bg-[#D32F2F] transition-colors"
                    data-testid="submit-live-button"
                  >
                    등록하기
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-bold rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    data-testid="cancel-live-button"
                  >
                    취소
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* My Lives */}
        <div>
          <h2 className="text-2xl font-black tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            내 라이브 스케줄
          </h2>
          {myLives.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="seller-lives-list">
              {myLives.map(live => (
                <LiveCard key={live.schedule_id} live={live} />
              ))}
            </div>
          ) : (
            <p className="text-gray-600 dark:text-gray-400" data-testid="no-seller-lives">
              등록된 라이브가 없습니다.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerDashboard;